import {
  GraphQLServerUrl,
  RxDatabase,
  RxReplicationWriteToMasterRow,
  WithDeleted,
} from 'rxdb'
import { replicateGraphQL } from 'rxdb/plugins/replication-graphql'

import { graphql } from '@/gql'
import {
  createQueryWithHash,
  pullResponseModifier,
} from '@/system/rxdb/replications/utils.ts'
import { AssessmentDetail } from '@/system/rxdb/schemas/assessmentDetail.ts'

type PullData =
  | {
      nodes: Array<WithDeleted<AssessmentDetail>>
    }
  | { assessmentDetail: WithDeleted<AssessmentDetail> }

// amount of items we want to pull max for each iteration
const BATCH_SIZE = 20

export const ASSESSMENT_DETAIL_FRAGMENT = /* GraphQL*/ `
    fragment assessmentDetail on AssessmentDetail {
        rowId
        updatedAt
        deleted
        assessmentId
        reportingResponsible
        assistanceNeeded
        assistances
        assistancesMobilityComments
        assistancesPositioningInBedComments
        assistancesPersonalHygieneComments
        assistancesDevicesToSupportExcretionComments
        assistancesPhysicalAidComments
        assistancesDevicesToFoodIntakeComments
        assistancesOtherComments
        assistanceServicesNeeded
        assistanceServices
        assistanceServicesOtherComments
        otherAssistancesNeeded
        otherAssistancesService
        otherAssistancesServiceOtherComments
        therapiesNeeded
        therapies
        therapiesOtherComments
        careWellOrganized
        organizationCareAdviceNeeded
        organizationCareAdviceTopics
        organizationCareAdviceComments
        movingDifficulties
        movingDifficultiesActivity
        movingDifficultiesActivityOtherComments
        sufficientMovingCare
        fallenCareRecently
        generallyPhysicalActive
        mobilityAreaAdvice
        mobilityAreaAdviceTopics
        mobilityAreaAdviceComments
        mentalDifficulties
        mentalDifficultiesList
        mentalDifficultiesListOtherComments
        behaviorChanges
        behaviorChangesList
        behaviorChangesListOtherComments
        changesSupportNeeded
        depressedRecently
        lessEnjoyment
        mentalHealthCounseling
        mentalHealthCounselingTopics
        mentalHealthCounselingComments
        physicalProblems
        physicalProblemsList
        physicalProblemsListOtherComments
        diseaseMeasures
        diseaseMeasuresList
        diseaseMeasuresListOtherComments
        diseaseMeasuresResponsible {
            medication
            medicationOtherComments
            bodyConditionMonitoring
            bodyConditionMonitoringOtherComments
            handlingBodyHuggingAids
            handlingBodyHuggingAidsOtherComments
            dressingWoundsCare
            dressingWoundsCareOtherComments
            therapy
            therapyOtherComments
            doctorVisits
            doctorVisitsOtherComments
            dietDueToIllness
            dietDueToIllnessOtherComments
            other
            otherOtherComments
        }
        sufficientProblemsSupport
        hospitalizedRecently
        signsOfHealthRisk
        signsOfHealthRiskList
        signsOfHealthRiskListSpecificRisksComments
        physicalHealthAdvice
        physicalHealthAdviceTopics
        physicalHealthAdviceComments
        activitySupport
        activitySupportList
        activitySupportListHygieneOtherComments
        activitySupportListDressingOtherComments
        activitySupportListNutritionOtherComments
        activitySupportListExcretionOtherComments
        activitySupportListHousekeepingOtherComments
        activitySupportPerson {
            washUpperBody
            washUpperBodyOtherComments
            combingShaving
            combingShavingOtherComments
            oralCare
            oralCareOtherComments
            washIntimateArea
            washIntimateAreaOtherComments
            shower
            showerOtherComments
            hairWash
            hairWashOtherComments
            hygieneOther
            hygieneOtherOtherComments
            dressingUpperBody
            dressingUpperBodyOtherComments
            dressingLowerBody
            dressingLowerBodyOtherComments
            dressingOther
            dressingOtherOtherComments
            drink
            drinkOtherComments
            eat
            eatOtherComments
            nutritionOther
            nutritionOtherOtherComments
            toilet
            toiletOtherComments
            urinaryIncontinence
            urinaryIncontinenceOtherComments
            fecalIncontinence
            fecalIncontinenceOtherComments
            excretionOther
            excretionOtherOtherComments
            shopping
            shoppingOtherComments
            cooking
            cookingOtherComments
            cleaning
            cleaningOtherComments
            housekeepingOther
            housekeepingOtherOtherComments
        }
        sufficientActivitiesSupport
        selfSufficiencyAdvice
        selfSufficiencyAdviceTopics
        selfSufficiencyAdviceComments
        socialContactDifficulties
        socialContactDifficultiesList
        socialContactDifficultiesListOtherComments
        socialContactSupport
        socialContactAdvice
        socialContactAdviceTopics
        socialContactAdviceComments
        livingEnvironmentProblems
        livingEnvironmentProblemsList
        livingEnvironmentProblemsListOtherComments
        livingEnvironmentSupport
        livingEnvironmentAdvice
        livingEnvironmentAdviceTopics
        livingEnvironmentAdviceComments
        relationshipIssues
        withRelativesIssues
        withRelativesIssuesPersonInNeed
        relativesRelationshipIssues
        relativesIssues
        careIssues
        careRelationshipAdvice
        careRelationshipAdviceTopics
        careRelationshipAdviceComments
        careSituation
        relativesCareDifficultiesList
        relativesCareDifficultiesListOtherComments
        relativesCareHelpFactorsList
        relativesCareHelpFactorsListOtherComments
        relativesCareAdvice
        relativesCareAdviceTopics
        relativesCareAdviceComments
    }
`
export const createAssessmentDetailReplication = (
  db: RxDatabase,
  url: GraphQLServerUrl,
  autoStart: boolean
) => {
  const assessmentDetailQuery = createQueryWithHash(
    graphql(/* GraphQL */ `
      query assessmentDetailDocuments(
        $checkpoint: DocumentCheckpointInput
        $first: Int!
      ) {
        assessmentDetailDocuments(checkpoint: $checkpoint, first: $first) {
          nodes {
            ...assessmentDetail
          }
        }
      }
    `)
  )
  const assessmentDetailStreamQuery = createQueryWithHash(
    graphql(/* GraphQL */ `
      subscription streamAssessmentDetails {
        streamAssessmentDetails {
          assessmentDetail {
            ...assessmentDetail
          }
        }
      }
    `)
  )
  const assessmentDetailPushQuery = createQueryWithHash(
    graphql(/* GraphQL*/ `
      mutation addAssessmentDetail($rows: [AssessmentDetailInput]!) {
        assessmentDetailReplicate(input: { items: $rows }) {
          result {
            ...assessmentDetail
          }
        }
      }
    `)
  )
  return replicateGraphQL({
    collection: db.assessment_detail,
    url,
    credentials: 'include',
    replicationIdentifier: url.http || '',
    autoStart,
    deletedField: 'deleted',
    pull: {
      includeWsHeaders: true,
      streamQueryBuilder: () => {
        return {
          ...assessmentDetailStreamQuery,
          variables: {},
        }
      },
      responseModifier: (data: PullData, origin) => {
        // in case of subscriptions, we always only receive a single assignment, so we map it here into the right
        // expected structure
        if (origin === 'stream' && data && 'assessmentDetail' in data) {
          return pullResponseModifier({ nodes: [data.assessmentDetail] })
        }
        return pullResponseModifier<AssessmentDetail>(
          data as {
            nodes: Array<WithDeleted<AssessmentDetail>>
          }
        )
      },
      batchSize: BATCH_SIZE,
      queryBuilder: (checkpoint, limit) => {
        return {
          ...assessmentDetailQuery,
          variables: {
            checkpoint,
            first: limit,
          },
        }
      },
    },
    push: {
      responseModifier: (data: { result: AssessmentDetail[] }) => {
        return data.result
      },
      queryBuilder(rows: RxReplicationWriteToMasterRow<AssessmentDetail>[]) {
        return {
          ...assessmentDetailPushQuery,
          variables: {
            rows: rows.map((row) => row.newDocumentState),
          },
        }
      },
    },
  })
}
